import React, {useState, useEffect, useRef} from "react";
import {Box, Paper, Pagination, TextField, InputAdornment, Typography, useTheme} from "@mui/material";
import CustomButton from "../../../core/components/ui/CustomButton";
import CustomTypography from "../../../core/components/ui/CustomTypography";
import AddIcon from "@mui/icons-material/Add";
import usePort from "../hooks/usePort";
import CustomTableLoaderComponent from "../../../core/components/common/custom-table-loader.component";
import ListPortComponent from "../components/list-port.component";
import {useNavigate} from "react-router-dom";
import usePortStore from "../zustand/usePortStore";
import CustomFilterComponent from "../../../core/components/common/custom-filter.component";
import { RBACUIGuard } from "../../RBACPlugin";
import { permissions } from "../../../core/components/common/variables/static_variables";


const headers = ["Port Name", "Type", "Location", "Status", "Actions"];

const PortPage = (props) => {
    const {isMobile, breakpoints} = props;

    const navigate = useNavigate();
    const theme = useTheme();

    const {isListingPort, portListWithPagination, getListPortWithPagination, 
        portTypeList, getPortTypeList, isFilteringPort, filterPort
    } = usePort();
    
    const {isPortStatusUpdated} = usePortStore();

    const [portList, setPortList] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);

    const [filterLabel, setFilterLabel] = useState(null);
    const [filter, setFilter] = useState(null);
    const [portNameFilterValue, setPortNameFilterValue] = useState(null);
    const [locationFilterValue, setLocationFilterValue] = useState(null);
    const [typeFilterValue, setTypeFilterValue] = useState(null);
    const [statusFilterValue, setStatusFilterValue] = useState(null);

    const [portTypeData, setPortTypeData] = useState([]);

    const rowsPerPage = 10;

    const searchForOptions = [
        {value: "port_name", label: "Port Name"},
        {value: "location", label: "Location"},
        {value: "type", label: "Type"},
        {value: "status", label: "Status"},
    ]

    const portStatusData = [
        {value: "Active", label: "Active"},
        {value: "Deactivated", label: "Deactivated"}
    ]

    // track previous filter values
    const prevFilter = useRef({ filter: null, type: null, status: null });

    useEffect(() => {
        const parameters = {
            page: 1,
            limit: rowsPerPage,
        }

        getListPortWithPagination(parameters);
        getPortTypeList();
    }, [])

    useEffect(() => {
        if (portTypeList) {
            const formattedPortTypes = portTypeList.map(type => ({value: type.toLowerCase(), label: type}));
            setPortTypeData(formattedPortTypes);
        }
    }, [portTypeList])

    useEffect(() => {
        if (portListWithPagination) {
            setPortList(portListWithPagination?.content);
            setTotalPages(portListWithPagination?.totalPages);
        }
    }, [portListWithPagination])

    useEffect(() => {
        if (currentPage || isPortStatusUpdated) {
            const parameters = {
                page: currentPage,
                limit: rowsPerPage,
            }
      
            getListPortWithPagination(parameters);
        }
    }, [currentPage, isPortStatusUpdated])

    useEffect(() => {
        if (filter && typeFilterValue) {
            const parameters = {
                page: 1,
                limit: 20,
                searchFor: filter,
                searchVal: typeFilterValue
            }
                
            filterPort(parameters);
            
        } else if (filter && statusFilterValue) {
            const parameters = {
                page: 1,
                limit: 20,
                searchFor: filter,
                searchVal: statusFilterValue
            }

            filterPort(parameters);
        }
    }, [typeFilterValue, statusFilterValue])


    const handleChangePagination = (event, page) => { 
        setCurrentPage(page); 
    }

    const handleClickOpen = () => {
        navigate("/ports/create");
    }

    const handleSearchForOption = (selectedOption, type) => { 
        if (selectedOption && type === "type") {
            setFilter(selectedOption.value);
            setFilterLabel(selectedOption.label);
            setStatusFilterValue(null);

        } else if (selectedOption && type === "status") {
            setFilter(selectedOption.value);
            setFilterLabel(selectedOption.label);
            setTypeFilterValue(null);

        } else {    
            handleSearchForClearFilter();
        }
    }

    const handleSearchForClearFilter = (e) => {
        setFilter(null);
        setFilterLabel(null);

        const parameters = {
            page: 1,
            limit: rowsPerPage,
        }

        getListPortWithPagination(parameters);
    }

    const handleSearchValOption = (selectedOption, type) => {
        if (selectedOption && type === "type") {
            setTypeFilterValue(selectedOption.label);
            setStatusFilterValue(null);

        } else if (selectedOption && type === "status") {
            setStatusFilterValue(selectedOption.label); 
            setTypeFilterValue(null);

        } else {    
            handleSearchValClearFilter();
        }
    }

    const handleSearchValClearFilter = (e) => {
        setTypeFilterValue(null);
        setStatusFilterValue(null);
        setPortNameFilterValue("");
        setLocationFilterValue("");

        const parameters = {
            page: 1,
            limit: rowsPerPage,
        }

        getListPortWithPagination(parameters);
    }

    const handleTextfield = (e, type) => {
        setTypeFilterValue(null);
        setStatusFilterValue(null);

        if (type === "portName") {
            setPortNameFilterValue(e.target.value);
            setLocationFilterValue("");

        } else if (type === "location") {
            setLocationFilterValue(e.target.value);
            setPortNameFilterValue("");
        }
    }

    const handleKeyPress = (e, type) => {
        if (e.keyCode === 13) {
            if (filter && type === "portName" && portNameFilterValue) {
                const parameters = {
                    page: 1,
                    limit: 20,
                    searchFor: filter,
                    searchVal: portNameFilterValue
                }

                filterPort(parameters);

            }  else if (filter && type === "location" && locationFilterValue) {
                const parameters = {
                    page: 1,
                    limit: 20,
                    searchFor: filter,
                    searchVal: locationFilterValue
                }

                filterPort(parameters);
            }
        }
    }


    return (
        <>        
            <Box sx={{textAlign: "start"}}>
                <CustomTypography 
                    variant="h3"
                    text={"Ports"}
                    customStyles={{
                        marginBottom: 0, 
                    }}
                />

                <CustomTypography 
                    variant="subtitle1"
                    text={"Manage and monitor all port operations seamlessly and efficiently"}
                    customStyles={{
                        marginBottom: 0, 
                    }}
                />
            </Box>
            
            <Paper elevation={0} sx={{border: `1px solid #e5edf6`, borderRadius: 2, padding: "24px 32px", mt: 3}}>
                <Box sx={{display: "flex", justifyContent: "right"}}>
                    <RBACUIGuard requiredPermission={permissions.ports.FILTER_LIST_PORT}>
                        <CustomFilterComponent
                            width={250}
                            isSearchable={true}
                            isClearable={true}
                            filterLabel={
                                    (filterLabel === "Type") ? "Type" 
                                :  
                                    (filterLabel === "Status") ? "Status" 
                                : 
                                    (filterLabel === "Port Name") ? "Port Name" 
                                :
                                    (filterLabel === "Location") ? "Location" 
                                :
                                    null
                            }
                            filterOptions={searchForOptions}
                            onChange={selectedOption => handleSearchForOption(selectedOption, filterLabel === "Type" ? "type" : "status")}
                            isSearchForFilter={true}
                            isSearchValue={false}
                        />
                    </RBACUIGuard>

                    <RBACUIGuard requiredPermission={permissions.ports.GET_PORT_TYPE_LIST}>
                        {(filterLabel === null || filterLabel === "Type") && 
                            <CustomFilterComponent
                                width={300}
                                isSearchable={true}
                                isClearable={true}
                                filterLabel={filterLabel}
                                filterOptions={portTypeData}
                                onChange={selectedOption => handleSearchValOption(selectedOption, "type")}
                                isSearchForFilter={false}
                                isSearchValue={true}
                                isDisabled={false}
                            />
                        }
                    </RBACUIGuard>

                    {(filterLabel === "Status") && 
                        <CustomFilterComponent
                            width={300}
                            isSearchable={true}
                            isClearable={true}
                            filterLabel={filterLabel}
                            filterOptions={portStatusData}
                            onChange={selectedOption => handleSearchValOption(selectedOption, "status")}
                            isSearchForFilter={false}
                            isSearchValue={true}
                            isDisabled={false}
                        />
                    }

                    {(filterLabel === "Port Name") &&
                        <TextField
                            value={portNameFilterValue}
                            placeholder={"Input Port Name..."}
                            variant="outlined"
                            autoComplete="off"  
                            onChange={(e) => handleTextfield(e, "portName")}
                            onKeyDown={(e) => handleKeyPress(e, "portName")}
                            slotProps={{
                                input: {
                                    style: {
                                        padding: "10px",
                                        fontSize: 14,
                                        fontStyle: "italic"
                                    },
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Typography sx={{fontSize: 14, color: theme.typography.primary, opacity: 0.85, fontStyle: "normal"}}>
                                                Port Name:
                                            </Typography>
                                        </InputAdornment>
                                    )
                                }
                            }}
                            sx={{
                                marginRight: "10px", 
                                "& .MuiInputBase-root": {
                                    height: 42, 
                                    backgroundColor: "transparent", 
                                    width: 350, 
                                    borderColor: "#dce8f5"
                                },
                                "& .MuiOutlinedInput-root": {
                                    height: 42,
                                    backgroundColor: "transparent",
                                    "& fieldset": {
                                        borderColor: "#dce8f5",
                                    },
                                    "&:hover fieldset": {
                                        borderColor: theme.palette.primary.main, 
                                    },
                                    "&.Mui-focused fieldset": {
                                        borderColor: `0 0 0 1px ${theme.palette.primary.main}`,
                                    }
                                },
                            }}  
                        /> 
                    }

                    {(filterLabel === "Location") &&
                        <TextField
                            value={locationFilterValue}
                            placeholder={"Input Location..."}
                            variant="outlined"
                            autoComplete="off"  
                            onChange={(e) => handleTextfield(e, "location")}
                            onKeyDown={(e) => handleKeyPress(e, "location")}
                            slotProps={{
                                input: {
                                    style: {
                                        padding: "10px",
                                        fontSize: 14,
                                        fontStyle: "italic"
                                    },
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Typography sx={{fontSize: 14, color: theme.typography.primary, opacity: 0.85, fontStyle: "normal"}}>
                                                Location:
                                            </Typography>
                                        </InputAdornment>
                                    )
                                }
                            }}
                            sx={{
                                marginRight: "10px", 
                                "& .MuiInputBase-root": {
                                    height: 42, 
                                    backgroundColor: "transparent", 
                                    width: 350, 
                                    borderColor: "#dce8f5"
                                },
                                "& .MuiOutlinedInput-root": {
                                    height: 42,
                                    backgroundColor: "transparent",
                                    "& fieldset": {
                                        borderColor: "#dce8f5",
                                    },
                                    "&:hover fieldset": {
                                        borderColor: theme.palette.primary.main, 
                                    },
                                    "&.Mui-focused fieldset": {
                                        borderColor: `0 0 0 1px ${theme.palette.primary.main}`,
                                    }
                                },
                            }}  
                        /> 
                    }

                    <RBACUIGuard requiredPermission={permissions.ports.CREATE_PORT}>
                        <CustomButton 
                            variant="contained" 
                            startIcon={<AddIcon fontSize="small" />}
                            label="Add Port"
                            customStyles={{fontSize: 14}}
                            onClick={handleClickOpen}
                        />
                    </RBACUIGuard>

                </Box>

                {(isListingPort || isFilteringPort) ?
                    <Box sx={{mt: 3}}>
                        <CustomTableLoaderComponent 
                            headers={headers} 
                            hasAction={false}
                        /> 
                    </Box>
                :   
                    <Box sx={{mt: 3, mb: (portList?.length === 0 || portList === undefined) ? 2 : {}}}>
                        <ListPortComponent 
                            headers={headers} 
                            data={portList} 
                            isMobile={isMobile}
                            breakpoints={breakpoints}
                        />
                
                        {portList?.length > 0 &&
                            <Pagination
                                count={totalPages}
                                page={currentPage}
                                onChange={handleChangePagination}
                                sx={{
                                    margin: "auto",
                                    width: "fit-content",
                                    paddingTop: 3,
                    
                                    "& .MuiPaginationItem-root.Mui-selected": {
                                        backgroundColor: "#0b99da",      
                                        color: "#fff",               
                                    }
                                }}
                            />
                        }
                    </Box>
                }
            </Paper>
       </>
    )
}

export default PortPage